import type { SVGProps } from "react"
import React from "react"
import { Button } from "@chakra-ui/react"
import { useUser } from "lib/user"
import type { VariantType } from "@shared/graphql.types"
import { navigate } from "utils/routes"
import debounce from "lodash.debounce"
import { addWishlistItemBySKU, removeWishlistItemBySKU } from "lib/api"
import { colours } from "@shared/ui.design-system"

type Props = {
  variant: VariantType
}

const WishlistIcon: React.FC<SVGProps<SVGSVGElement>> = ({ fill }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="19"
      height="18"
      fill="currentColor"
      viewBox="0 0 19 18"
    >
      <path
        d="M3.33203 11.7708L8.83203 16.2708L10.832 15.7708L15.832 11.2708L17.332 5.77075L15.832 1.77075L12.832 1.27075L9.33203 3.27075H8.83203V2.77075L5.33203 1.27075L2.33203 2.77075L1.33203 7.27075L3.33203 11.7708Z"
        fill={fill}
      />
      <path
        d="M13.6237 0.520752C16.4085 0.520752 18.6654 2.81242 18.6654 6.02075C18.6654 12.4374 11.7904 16.1041 9.4987 17.4791C7.20703 16.1041 0.332031 12.4374 0.332031 6.02075C0.332031 2.81242 2.6237 0.520752 5.3737 0.520752C7.0787 0.520752 8.58203 1.43742 9.4987 2.35409C10.4154 1.43742 11.9187 0.520752 13.6237 0.520752ZM10.3549 14.8244C11.1624 14.3148 11.8912 13.8078 12.5732 13.2652C15.3058 11.0927 16.832 8.7185 16.832 6.02075C16.832 3.85742 15.4231 2.35409 13.6237 2.35409C12.6374 2.35409 11.5704 2.87659 10.7949 3.65025L9.4987 4.94642L8.20253 3.65025C7.42703 2.87659 6.36003 2.35409 5.3737 2.35409C3.59536 2.35409 2.16536 3.87209 2.16536 6.02075C2.16536 8.71942 3.69253 11.0927 6.42328 13.2652C7.1062 13.8078 7.83495 14.3148 8.64253 14.8235C8.91661 14.9968 9.18795 15.1627 9.4987 15.3478C9.80945 15.1627 10.0808 14.9968 10.3549 14.8244Z"
        fill={fill}
      />
    </svg>
  )
}

export const WishlistButton: React.FC<Props> = ({ variant }) => {
  const { wishlist, isLoggedIn } = useUser()

  const isInWishlist = wishlist?.some(
    (item) => item.variant?.sku === variant.sku
  )

  const handleWishlist = () => {
    if (!isLoggedIn) {
      navigate("/login")
      return
    }
    handleAddRemoveToWishlist()
  }

  const handleAddRemoveToWishlist = debounce(() => {
    if (isInWishlist) removeWishlistItemBySKU(variant.sku)
    else addWishlistItemBySKU(variant.sku)
  }, 500)

  return (
    <Button
      title={isInWishlist ? "Remove from wishlist" : "Add to wishlist"}
      variant="primary"
      borderStartRadius="0"
      onClick={handleWishlist}
    >
      {!isInWishlist ? (
        <WishlistIcon fill={colours.secondary.light} />
      ) : (
        <WishlistIcon fill={colours.accent.medium} />
      )}
    </Button>
  )
}
